<script setup lang="ts">
/**
 * TreeView — Reusable expandable tree component
 *
 * Props:
 *   nodes       — Root-level tree nodes
 *   loadChildren — Async function to fetch children for a node
 *   expanded    — Set of expanded node IDs
 *   selectedId  — Currently selected node ID
 *
 * Events:
 *   toggle — Emitted when a node expand/collapse is toggled
 *   select — Emitted when a leaf/selectable node is clicked
 */

export interface TreeNode {
  id: number;
  label: string;
  hasChildren: boolean;
  children?: TreeNode[];
  icon?: string;
  data?: unknown;
}

const props = defineProps<{
  nodes: TreeNode[];
  loadChildren?: (parentId: number) => Promise<TreeNode[]>;
  expanded: Set<number>;
  selectedId: number | null;
  depth?: number;
}>();

const emit = defineEmits<{
  toggle: [id: number];
  select: [id: number, node: TreeNode];
}>();

import { ref } from 'vue';

const loadingNodes = ref<Set<number>>(new Set());
const loadedChildren = ref<Map<number, TreeNode[]>>(new Map());

async function handleClick(node: TreeNode): Promise<void> {
  if (node.hasChildren) {
    emit('toggle', node.id);

    // Lazy-load children if not yet loaded
    if (!loadedChildren.value.has(node.id) && !node.children?.length && props.loadChildren) {
      loadingNodes.value.add(node.id);
      try {
        const children = await props.loadChildren(node.id);
        loadedChildren.value.set(node.id, children);
      } finally {
        loadingNodes.value.delete(node.id);
      }
    }
  }

  // Always emit select (parent nodes can also be selectable)
  emit('select', node.id, node);
}

function getChildren(node: TreeNode): TreeNode[] {
  return node.children ?? loadedChildren.value.get(node.id) ?? [];
}

function isExpanded(id: number): boolean {
  return props.expanded.has(id);
}

function isLoading(id: number): boolean {
  return loadingNodes.value.has(id);
}

const currentDepth = props.depth ?? 0;
</script>

<template>
  <div class="tree-view" :class="{ 'tree-root': currentDepth === 0 }">
    <div
      v-for="node in nodes"
      :key="node.id"
      class="tree-node"
    >
      <div
        class="tree-item"
        :class="{
          'tree-item--selected': selectedId === node.id,
          'tree-item--expanded': isExpanded(node.id),
        }"
        :style="{ paddingLeft: `${8 + currentDepth * 16}px` }"
        @click="handleClick(node)"
      >
        <!-- Expand/collapse indicator -->
        <span v-if="node.hasChildren" class="tree-expander">
          <svg
            v-if="isLoading(node.id)"
            class="tree-spinner"
            width="12" height="12" viewBox="0 0 12 12"
          >
            <circle cx="6" cy="6" r="5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-dasharray="20" stroke-linecap="round" />
          </svg>
          <svg
            v-else
            class="tree-chevron"
            :class="{ 'tree-chevron--open': isExpanded(node.id) }"
            width="12" height="12" viewBox="0 0 12 12"
          >
            <path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
        <span v-else class="tree-expander tree-expander--leaf" />

        <!-- Label -->
        <span class="tree-label">{{ node.label }}</span>
      </div>

      <!-- Children (recursive) -->
      <div v-if="node.hasChildren && isExpanded(node.id)" class="tree-children">
        <TreeView
          :nodes="getChildren(node)"
          :load-children="loadChildren"
          :expanded="expanded"
          :selected-id="selectedId"
          :depth="currentDepth + 1"
          @toggle="(id: number) => emit('toggle', id)"
          @select="(id: number, n: TreeNode) => emit('select', id, n)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.tree-root {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.tree-node {
  user-select: none;
}

.tree-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  cursor: pointer;
  color: var(--text-primary);
  transition: background var(--transition-fast);
  white-space: nowrap;
  overflow: hidden;
}

.tree-item:hover {
  background: var(--bg-hover);
}

.tree-item--selected {
  background: rgba(74, 158, 255, 0.15);
  color: var(--accent-blue);
}

.tree-expander {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: var(--text-secondary);
}

.tree-expander--leaf {
  width: 16px;
}

.tree-chevron {
  transition: transform var(--transition-fast);
}

.tree-chevron--open {
  transform: rotate(90deg);
}

.tree-spinner {
  animation: tree-spin 0.8s linear infinite;
}

@keyframes tree-spin {
  to { transform: rotate(360deg); }
}

.tree-label {
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.85rem;
  line-height: 1.4;
}

.tree-children {
  /* Children inherit layout from parent */
}
</style>
